import React, { useState, useEffect } from 'react';
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { X, ChevronRight, CheckCircle2, Heart, Truck, Gift } from 'lucide-react';

const steps = [
    {
        title: "Welcome to Say Whaat?",
        description: "Thank you for joining our mission to share with dignity. Let's get you started on your giving journey.",
        icon: <Heart className="w-12 h-12 text-red-500 mb-4" />
    },
    {
        title: "How it Works",
        description: "1. You Schedule a Donation\n2. We Pickup from your Doorstep\n3. We Distribute to those in need.",
        icon: <Truck className="w-12 h-12 text-blue-500 mb-4" />
    },
    {
        title: "Track Your Impact",
        description: "See exactly where your donations go and earn badgets for your generosity.",
        icon: <Gift className="w-12 h-12 text-purple-500 mb-4" />
    }
];

export default function OnboardingGuide() {
    const [showWelcome, setShowWelcome] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
        if (!hasSeenOnboarding) {
            setShowWelcome(true);
        }
    }, []);

    const startTour = () => {
        setShowWelcome(false);
        localStorage.setItem('hasSeenOnboarding', 'true');

        const isMobile = window.innerWidth < 768;

        const driverObj = driver({
            showProgress: true,
            popoverClass: 'driverjs-theme', // Apply custom theme
            steps: [
                {
                    element: '#tour-hero',
                    popover: {
                        title: '👋 Welcome to Say Whaat!',
                        description: 'This is your hero dashboard. Here you can see your basic profile information and quickly start new donations.',
                        side: "bottom",
                        align: isMobile ? 'center' : 'start'
                    }
                },
                {
                    element: '#tour-create-btn',
                    popover: {
                        title: '🎁 Make a Donation',
                        description: 'Ready to share with dignity? Click here to start your garments or monetary donation.',
                        side: "bottom",
                        align: isMobile ? 'center' : 'start'
                    }
                },
                {
                    element: '#tour-stats',
                    popover: {
                        title: '📊 Track Your Impact',
                        description: 'Keep tabs on your total donations, amounts given, and confirmed life deliveries.',
                        side: "bottom",
                        align: isMobile ? 'center' : 'start'
                    }
                },
                {
                    element: '#tour-history-link',
                    popover: {
                        title: '📜 Donation History',
                        description: 'Monitor your recent donation requests and follow their live tracking progress here.',
                        side: isMobile ? "top" : "top",
                        align: isMobile ? 'center' : 'start'
                    }
                }
            ]
        });

        driverObj.drive();
    };

    const handleSkip = () => {
        setShowWelcome(false);
        localStorage.setItem('hasSeenOnboarding', 'true');
    };

    if (!showWelcome) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
            <Card className="w-full max-w-sm sm:max-w-md bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 shadow-2xl overflow-hidden relative">
                <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                    onClick={handleSkip}
                >
                    <X className="w-4 h-4" />
                </Button>

                <CardContent className="pt-10 pb-4 sm:pt-12 sm:pb-6 min-h-[280px] sm:min-h-[300px] flex flex-col items-center text-center px-6 sm:px-8">
                    <div className="flex-1 flex flex-col items-center justify-center animate-in slide-in-from-right-8 fade-in duration-300" key={currentSlide}>
                        <div className="scale-90 sm:scale-100">{steps[currentSlide].icon}</div>
                        <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3 text-zinc-900 dark:text-white">{steps[currentSlide].title}</h2>
                        <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400 whitespace-pre-line leading-relaxed px-1 sm:px-2">
                            {steps[currentSlide].description}
                        </p>
                    </div>

                    <div className="flex gap-2 mt-6 sm:mt-8">
                        {steps.map((_, idx) => (
                            <div
                                key={idx}
                                className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${idx === currentSlide ? 'w-6 sm:w-8 bg-zinc-900 dark:bg-white' : 'w-1.5 sm:w-2 bg-zinc-200 dark:bg-zinc-800'}`}
                            />
                        ))}
                    </div>
                </CardContent>

                <CardFooter className="flex justify-between border-t border-zinc-100 dark:border-zinc-800 p-4 sm:p-6 bg-zinc-50 dark:bg-zinc-900/50">
                    <Button variant="ghost" onClick={handleSkip} className="text-zinc-500 text-sm sm:text-base">
                        Skip
                    </Button>

                    {currentSlide < steps.length - 1 ? (
                        <Button onClick={() => setCurrentSlide(prev => prev + 1)} className="bg-zinc-900 dark:bg-white text-white dark:text-black hover:bg-zinc-800 dark:hover:bg-zinc-200 text-sm sm:text-base">
                            Next <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    ) : (
                        <Button onClick={startTour} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20 text-sm sm:text-base">
                            Start Tour <CheckCircle2 className="w-4 h-4 ml-2" />
                        </Button>
                    )}
                </CardFooter>
            </Card>
        </div>
    );
}
